import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaSrc } from "@/lib/api";
import { inp } from "@/pages/lessons/ActivityEditor";
import { StepVoicePicker } from "@/pages/lessons/StepVoicePicker";
import { Trash2, X, Plus, ImagePlus, Loader2, ChevronUp, ChevronDown, BookOpen, ListChecks, Lightbulb, HelpCircle, PenLine } from "lucide-react";
import { STROKE_IDS, SHAPE_IDS } from "@/data/printGeometry";

const STEP_TYPES = [
  { id: "teach", label: "Teach", Icon: BookOpen },
  { id: "example", label: "Worked example", Icon: ListChecks },
  { id: "demo", label: "Handwriting demo", Icon: PenLine },
  { id: "concept", label: "Concept card", Icon: Lightbulb },
  { id: "check", label: "Quick check", Icon: HelpCircle },
];

const blankStep = (type) => ({
  teach: { type: "teach", title: "", body: "", bullets: [], speak: true, media_url: "" },
  example: { type: "example", title: "", body: "", steps: [""], speak: true, media_url: "" },
  demo: { type: "demo", title: "", body: "", trace: { kind: "letter", text: "A", style: "print" }, speak: true, media_url: "" },
  concept: { type: "concept", term: "", definition: "", speak: true, media_url: "" },
  check: { type: "check", title: "", check: { question: "", options: ["", ""], answer: 0 }, speak: true, media_url: "" },
}[type]);

function StepCard({ step, idx, total, illustrateCtx, defaultVoice, onChange, onRemove, onMove }) {
  const [busy, setBusy] = useState(false);
  const set = (patch) => onChange({ ...step, ...patch });
  const meta = STEP_TYPES.find((t) => t.id === step.type);

  const illustrate = async () => {
    const base = step.title || step.term || step.body || illustrateCtx;
    if (!base) { toast.error("Add a title first so AI knows what to draw."); return; }
    setBusy(true);
    try {
      const { media_url } = await api.illustrate({ prompt: `${illustrateCtx}: ${base}` });
      set({ media_url });
      toast.success("Illustration added!");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-[#FAF7F2] p-4" data-testid={`tstep-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#17BEBB]/15 px-2.5 py-1 text-xs font-bold text-[#0B7F74]">
          {meta && <meta.Icon size={13} />} {meta?.label}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="rounded p-1 text-[#6B7280] hover:bg-[#F2ECE1] disabled:opacity-30"><ChevronUp size={15} /></button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} className="rounded p-1 text-[#6B7280] hover:bg-[#F2ECE1] disabled:opacity-30"><ChevronDown size={15} /></button>
          <button onClick={onRemove} data-testid={`tstep-remove-${idx}`} className="rounded p-1 text-[#E05A47] hover:bg-[#E05A47]/10"><Trash2 size={15} /></button>
        </div>
      </div>

      {step.type !== "concept" && (
        <input value={step.title} onChange={(e) => set({ title: e.target.value })} placeholder="Step title" className={`${inp} mt-3 font-semibold`} />
      )}

      {step.type === "teach" && (
        <div className="mt-2 space-y-2">
          <textarea value={step.body} onChange={(e) => set({ body: e.target.value })} placeholder="What you're teaching (read aloud to the child)" className={`${inp} h-20 resize-none`} />
          <div className="text-xs font-bold text-[#6B7280]">Key points (optional)</div>
          {(step.bullets || []).map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={b} onChange={(e) => { const bb = [...step.bullets]; bb[i] = e.target.value; set({ bullets: bb }); }} placeholder={`Point ${i + 1}`} className={inp} />
              <button onClick={() => set({ bullets: step.bullets.filter((_, j) => j !== i) })} className="text-[#E05A47]"><X size={14} /></button>
            </div>
          ))}
          <button onClick={() => set({ bullets: [...(step.bullets || []), ""] })} className="text-sm font-bold text-[#0D9488]">+ Add point</button>
        </div>
      )}

      {step.type === "example" && (
        <div className="mt-2 space-y-2">
          <input value={step.body} onChange={(e) => set({ body: e.target.value })} placeholder="The problem (e.g. 2 + 3 = ?)" className={inp} />
          <div className="text-xs font-bold text-[#6B7280]">Solution steps (revealed one at a time)</div>
          {(step.steps || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#0D9488]">{i + 1}</span>
              <input value={s} onChange={(e) => { const ss = [...step.steps]; ss[i] = e.target.value; set({ steps: ss }); }} placeholder={`Step ${i + 1}`} className={inp} />
              {step.steps.length > 1 && <button onClick={() => set({ steps: step.steps.filter((_, j) => j !== i) })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          <button onClick={() => set({ steps: [...(step.steps || []), ""] })} className="text-sm font-bold text-[#0D9488]">+ Add step</button>
        </div>
      )}

      {step.type === "demo" && (
        <div className="mt-2 space-y-2">
          <input value={step.body} onChange={(e) => set({ body: e.target.value })} placeholder="What Zoodie says while showing it (e.g. Start at the top and pull down!)" className={inp} />
          <div className="grid gap-2 sm:grid-cols-3">
            <select value={step.trace?.kind || "letter"} onChange={(e) => set({ trace: { ...step.trace, kind: e.target.value, text: e.target.value === "stroke" ? STROKE_IDS[0] : e.target.value === "shape" ? SHAPE_IDS[0] : "" } })} className={inp}>
              {[["stroke", "Pre-writing stroke"], ["shape", "Shape"], ["letter", "Letter"], ["word", "Word"], ["sentence", "Sentence"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            {step.trace?.kind === "stroke" || step.trace?.kind === "shape" ? (
              <select value={step.trace?.text || ""} onChange={(e) => set({ trace: { ...step.trace, text: e.target.value } })} className={inp}>
                {(step.trace?.kind === "stroke" ? STROKE_IDS : SHAPE_IDS).map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
              </select>
            ) : (
              <input value={step.trace?.text || ""} onChange={(e) => set({ trace: { ...step.trace, text: e.target.value } })} placeholder="Letter, word or short sentence" className={inp} />
            )}
            <select value={step.trace?.style || "print"} onChange={(e) => set({ trace: { ...step.trace, style: e.target.value } })} className={inp}>
              <option value="print">Print letters</option>
              <option value="cursive">Cursive letters</option>
            </select>
          </div>
        </div>
      )}

      {step.type === "concept" && (
        <div className="mt-3 space-y-2">
          <input value={step.term} onChange={(e) => set({ term: e.target.value })} placeholder="Word / term" className={`${inp} font-semibold`} />
          <textarea value={step.definition} onChange={(e) => set({ definition: e.target.value })} placeholder="Kid-friendly definition" className={`${inp} h-16 resize-none`} />
        </div>
      )}

      {step.type === "check" && (
        <div className="mt-2 space-y-2">
          <input value={step.check.question} onChange={(e) => set({ check: { ...step.check, question: e.target.value } })} placeholder="Question" className={inp} />
          {step.check.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name={`tcheck-${idx}`} checked={step.check.answer === i} onChange={() => set({ check: { ...step.check, answer: i } })} />
              <input value={o} onChange={(e) => { const op = [...step.check.options]; op[i] = e.target.value; set({ check: { ...step.check, options: op } }); }} placeholder={`Option ${i + 1}${step.check.answer === i ? " (correct)" : ""}`} className={inp} />
              {step.check.options.length > 2 && <button onClick={() => set({ check: { ...step.check, options: step.check.options.filter((_, j) => j !== i), answer: 0 } })} className="text-[#E05A47]"><X size={14} /></button>}
            </div>
          ))}
          {step.check.options.length < 4 && <button onClick={() => set({ check: { ...step.check, options: [...step.check.options, ""] } })} className="text-sm font-bold text-[#0D9488]">+ Add option</button>}
        </div>
      )}

      {/* voice / mic — pick AI voice or record your own for this step */}
      <StepVoicePicker
        step={step}
        onChange={(ns) => onChange(ns)}
        defaultVoice={defaultVoice}
        bodyText={
          step.type === "teach"   ? step.body :
          step.type === "example" ? [step.body, ...(step.steps || [])].filter(Boolean).join(". ") :
          step.type === "demo"    ? step.body || step.title :
          step.type === "concept" ? [step.term, step.definition].filter(Boolean).join(": ") :
          step.type === "check"   ? [step.check?.question, ...(step.check?.options || [])].filter(Boolean).join(". ") :
          ""
        }
        testid={`tstep-voice-${idx}`}
      />

      {/* illustration */}
      <div className="mt-3 flex items-center gap-3">
        {step.media_url
          ? <img src={mediaSrc(step.media_url)} alt="" className="h-16 w-16 rounded-xl border border-[#E5DECF] object-cover" data-testid={`tstep-img-${idx}`} />
          : <div className="flex h-16 w-16 items-center justify-center rounded-xl border border-dashed border-[#E5DECF] text-[#94A3B8]"><ImagePlus size={20} /></div>}
        <div className="flex items-center gap-2">
          <button onClick={illustrate} disabled={busy} data-testid={`tstep-illustrate-${idx}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#652BDB] disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} {step.media_url ? "Regenerate" : "Illustrate with AI"}
          </button>
          {step.media_url && <button onClick={() => set({ media_url: "" })} className="text-xs font-bold text-[#E05A47]">Remove</button>}
        </div>
      </div>
    </div>
  );
}

export default function TutorialEditor({ tutorial, onChange, illustrateCtx, defaultVoice }) {
  const steps = tutorial?.steps || [];
  const setSteps = (s) => onChange({ steps: s });
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const s = [...steps]; [s[i], s[j]] = [s[j], s[i]]; setSteps(s);
  };
  return (
    <div className="space-y-3" data-testid="tutorial-editor">
      <div className="flex flex-wrap gap-2">
        {STEP_TYPES.map((t) => (
          <button key={t.id} onClick={() => setSteps([...steps, blankStep(t.id)])} data-testid={`add-tstep-${t.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-white px-3 py-1.5 text-xs font-bold text-[#0B7F74] hover:bg-[#17BEBB]/10">
            <Plus size={13} /> {t.label}
          </button>
        ))}
      </div>
      {steps.map((s, i) => (
        <StepCard key={i} step={s} idx={i} total={steps.length} illustrateCtx={illustrateCtx} defaultVoice={defaultVoice}
          onChange={(ns) => setSteps(steps.map((x, j) => (j === i ? ns : x)))}
          onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
          onMove={(dir) => move(i, dir)} />
      ))}
      {steps.length === 0 && <p className="font-body text-sm text-[#94A3B8]">No teaching steps yet. Generate with AI above, or add a step type.</p>}
    </div>
  );
}
